import type Decimal from 'decimal.js';
import { D, RATE_UI_SCALE, fmtAmt, type CcyCode, type Money } from '@wave/shared';

/**
 * Диаграмма «Поток денег»: Клиент → WAVE → Исполнитель, 4 нумерованных шага.
 * Используется в форме создания заявки (превью) и в карточке заявки (факт).
 *
 * В карточке шаги маркируются выполненностью через `doneSteps`:
 * выполненный шаг «гаснет» — номер, линия и суммы серые; ожидающий остаётся
 * цветным (зелёный приход / красный расход). Взгляд сразу цепляется за то,
 * что осталось сделать. Размер блока не меняется.
 *
 * Маппинг шагов на проводки (idx 0..3 модели [clGive−, clGet+, exGet+, exGive−]):
 *   шаг 1 = клиент → WAVE (idx 0) · шаг 2 = WAVE → исполнитель (idx 2)
 *   шаг 3 = исполнитель → WAVE (idx 3) · шаг 4 = WAVE → клиент (idx 1)
 */
export const FLOW_STEP_TO_PROVODKA_IDX: Record<number, number> = { 1: 0, 2: 2, 3: 3, 4: 1 };

export function FlowSteps({
  clGiveAmt,
  clGiveCcy,
  clGetAmt,
  clGetCcy,
  exGetAmt,
  exGetCcy,
  receiveUsdt,
  payUsdt,
  profitUsdt,
  giveRate,
  payRate,
  isLoss,
  isNearZero,
  doneSteps,
  coefUsdt,
  fixUsdt,
  bonusUsdt,
}: {
  clGiveAmt: string;
  clGiveCcy: CcyCode;
  clGetAmt: string;
  clGetCcy: CcyCode;
  exGetAmt: string;
  exGetCcy: CcyCode;
  receiveUsdt: Decimal;
  payUsdt: Decimal;
  profitUsdt: Decimal;
  giveRate: Money;
  payRate: Money;
  isLoss: boolean;
  isNearZero: boolean;
  /** Выполненность по номеру шага (1..4). Не передан = режим превью, всё контурное. */
  doneSteps?: Record<number, boolean>;
  /**
   * USDT-эквивалент дельты коэффициента (наценка рефереру) внутри шага 1.
   * Шаг 1 показывает полную сумму к получению, но дельта — транзит и в профит
   * не входит: «Профит = шаг 1 − коэфф. − шаг 2».
   */
  coefUsdt?: Decimal | null;
  /**
   * Фикс за платёжку в USDT. Симметричный транзит: входит в шаг 1 и шаг 2
   * одинаково и в профите сокращается, поэтому в формулу не вычитается — лишь
   * помечается «транзитом», чтобы было видно, что суммы шагов выросли на фикс.
   */
  fixUsdt?: Money | null;
  /**
   * Бонус исполнителю в USDT. Асимметричный: входит ТОЛЬКО в шаг 2 (оплата
   * исполнителю), поэтому «шаг 1 − шаг 2» уже даёт профит нетто. Отдельно не
   * вычитается — помечается «из профита», чтобы было видно, что шаг 2 вырос на бонус.
   */
  bonusUsdt?: Money | null;
}) {
  const usdtPay = exGetCcy === 'USDT';
  const done = (n: number) => doneSteps?.[n] ?? false;
  return (
    <div className="font-mono text-[11px] tabular-nums text-text">
      <div className="grid grid-cols-[auto_1fr_auto_1fr_auto] items-center gap-x-3">
        <FlowNode title="Клиент" />
        <FlowEdge
          forward={{
            step: 1,
            amt: fmtAmt(clGiveAmt, clGiveCcy),
            rateNote: clGiveCcy === 'USDT' ? null : `÷ ${D(giveRate).toDecimalPlaces(RATE_UI_SCALE)}`,
            usdt: `${receiveUsdt.toFixed(2)} USDT`,
            tone: 'in',
            done: done(1),
          }}
          backward={{ step: 4, amt: fmtAmt(clGetAmt, clGetCcy), tone: 'out', done: done(4) }}
        />
        <FlowNode title="WAVE" highlight />
        <FlowEdge
          forward={{
            step: 2,
            amt: fmtAmt(exGetAmt, exGetCcy),
            rateNote: usdtPay ? null : `÷ ${D(payRate).toDecimalPlaces(RATE_UI_SCALE)}`,
            usdt: `${payUsdt.toFixed(2)} USDT`,
            tone: 'out',
            done: done(2),
          }}
          backward={{ step: 3, amt: fmtAmt(clGetAmt, clGetCcy), tone: 'in', done: done(3) }}
        />
        <FlowNode title="Исполнитель" />
      </div>

      <div className="mt-3 flex items-center gap-2 border-t border-border-subtle pt-2">
        <span className="text-text2">
          {`${
            coefUsdt && !coefUsdt.isZero()
              ? `Профит = шаг 1 − коэфф. (${coefUsdt.gte(0) ? '' : '−'}${coefUsdt.abs().toFixed(2)} USDT транзитом) − шаг 2`
              : 'Профит = шаг 1 − шаг 2'
          }${fixUsdt && !D(fixUsdt).isZero() ? ` · фикс ${fmtAmt(fixUsdt, 'USDT')} USDT транзитом` : ''}${bonusUsdt && !D(bonusUsdt).isZero() ? ` · бонус ${fmtAmt(bonusUsdt, 'USDT')} USDT исполнителю из профита` : ''}:`}
        </span>
        <span className="ml-auto font-semibold">
          {isNearZero ? (
            <span className="text-accent">≈ 0 USDT</span>
          ) : (
            <span className={isLoss ? 'text-danger' : 'text-success'}>
              {profitUsdt.gte(0) ? '+' : ''}
              {profitUsdt.toFixed(2)} USDT
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

function FlowNode({ title, highlight }: { title: string; highlight?: boolean }) {
  return (
    <div
      className={`min-w-[88px] rounded-md border px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-wide ${
        highlight
          ? 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent'
          : 'border-border bg-bg2 text-text'
      }`}
    >
      {title}
    </div>
  );
}

interface FlowLeg {
  step: number;
  amt: string;
  rateNote?: string | null;
  usdt?: string | null;
  /** in = деньги приходят к нам (зелёный), out = уходят от нас (красный). */
  tone: 'in' | 'out';
  /** Проводка шага подтверждена — заливка бейджа + сплошная линия. */
  done?: boolean;
}

function FlowEdge({ forward, backward }: { forward: FlowLeg; backward: FlowLeg }) {
  return (
    <div className="flex flex-col items-stretch text-center">
      {/* Верхняя стрелка вправо: основной поток */}
      <FlowLegRow leg={forward} />
      <ArrowLine direction="right" tone={forward.tone} done={forward.done} />

      {/* Нижняя стрелка влево: возврат валюты исполнителем */}
      <ArrowLine direction="left" tone={backward.tone} done={backward.done} />
      <FlowLegRow leg={backward} />
    </div>
  );
}

function FlowLegRow({ leg }: { leg: FlowLeg }) {
  const color = leg.done ? 'text-text-subtle' : leg.tone === 'in' ? 'text-success' : 'text-danger';
  return (
    <div className="flex items-baseline justify-center gap-1.5 leading-tight">
      <StepBadge n={leg.step} tone={leg.tone} done={leg.done} />
      <span className={`font-semibold ${color}`}>{leg.amt}</span>
      {leg.rateNote && <span className="text-text-subtle">{leg.rateNote}</span>}
      {leg.usdt && (
        <span className={leg.done ? 'text-text-subtle' : 'text-text2'}>
          = <span className={`font-semibold ${color}`}>{leg.usdt}</span>
        </span>
      )}
    </div>
  );
}

function StepBadge({ n, tone, done }: { n: number; tone: 'in' | 'out'; done?: boolean }) {
  // Выполнено = «погасший» серый бейдж; ожидает = цветной, как в превью формы.
  const cls = done
    ? 'border-border-strong bg-bg3 text-text-subtle'
    : tone === 'in'
      ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-success'
      : 'border-[var(--red-border)] bg-[var(--red-bg)] text-danger';
  return (
    <span
      className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[9px] font-semibold ${cls}`}
      title={done ? 'Проводка подтверждена' : undefined}
    >
      {n}
    </span>
  );
}

function ArrowLine({
  direction,
  tone,
  done,
}: {
  direction: 'right' | 'left';
  tone: 'in' | 'out';
  done?: boolean;
}) {
  const colorCls = done ? 'text-border-strong' : tone === 'in' ? 'text-success' : 'text-danger';
  const head = (
    <svg width="10" height="10" viewBox="0 0 10 10" className={`shrink-0 ${colorCls}`} aria-hidden="true">
      {direction === 'right' ? (
        <path d="M1 5h7M5 1l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      ) : (
        <path d="M9 5H2M5 1L1 5l4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      )}
    </svg>
  );
  return (
    <div className={`my-0.5 flex items-center ${colorCls}`}>
      {direction === 'left' && head}
      <div className={`h-px flex-1 bg-current ${done ? 'opacity-100' : 'opacity-60'}`} />
      {direction === 'right' && head}
    </div>
  );
}
